import { ColDef, ColDefField, ValueGetterParams } from 'ag-grid-community';
import { TFunction } from 'i18next';
import ModelActionsCellRenderer, {
  ModelActionsCellRendererParams,
} from '../cells/model-actions/ModelActionsCellRenderer';
import { ModelWithId } from '@casops/store/apis/commonModels';
import { ReportingItem } from '@casops/hooks/assets/useAssetReportingTableData';
import { getComponentName, getSelfTestCategory } from '@casops/shared/utils/translationHelpers';
import FileStatusRenderer from '@casops/components/tables/cells/file-status/FileStatusRenderer';
import ConfigStatusRenderer from '@cas/components/tables/cells/config-status/ConfigStatusCellRenderer';
import { AssetNameData } from '@casops/hooks/assets/useAssetNameTableData';
import DurationRenderer from '../cells/duration/DurationRenderer';
import FaultLevelCellRenderer from '../cells/fault-level/FaultLevelCellRenderer';
import {
  AssetComponentModel,
  AssetFaultDisplayModel,
  AssetFileDeviceModel,
} from '@casops/store/apis/registeredAssetsApi';

export const lockColumn = {
  sortable: false,
  lockPinned: true,
  suppressMovable: true,
  resizable: false,
};

// The purpose of this generic interface is to constrain the cellRendererParams property because
// AG Grid does not offer any means of narrowing the 'any' type of the ColDef.cellRendererParams property.
export interface ColDefWithCellRendererParams<TData extends object, TParams extends object>
  extends ColDef<TData> {
  cellRendererParams: TParams;
}

export const columnDefModelActions = <TData extends ModelWithId, TQueryArg = ModelWithId>(
  t: TFunction,
  cellRendererParams: ModelActionsCellRendererParams<TData, TQueryArg>,
): ColDefWithCellRendererParams<TData, ModelActionsCellRendererParams<TData, TQueryArg>> => ({
  headerName: t('Actions'),
  cellRenderer: ModelActionsCellRenderer,
  headerClass: 'ag-right-aligned-header',
  cellRendererParams,
  ...lockColumn,
  comparator: undefined,
});

type ComponentDefFunc = (t: TFunction) => ColDef<AssetComponentModel>;
type ReportingColDefFunc = (t: TFunction) => ColDef<ReportingItem>;
type AssetFileColDefFunc = (t: TFunction) => ColDef<AssetFileDeviceModel>;

export const columnDefAssetReportingEvent: ReportingColDefFunc = t => ({
  field: 'evt',
  headerName: t('Event'),
});

export const columnDefAssetReportingDate: ReportingColDefFunc = t => ({
  field: 'date',
  headerName: t('Date'),
});

export const columnDefAssetReportingTime: ReportingColDefFunc = t => ({
  field: 'time',
  headerName: t('Time'),
});

export const columnDefAssetReportingWhen: ReportingColDefFunc = t => ({
  field: 'when',
  headerName: t('Fields.When'),
});

export const columnDefBootloaderVersion: ComponentDefFunc = t => ({
  field: 'bootVer',
  headerName: t('Fields.BootloaderVersion'),
});

export const columnDefComponentId: ComponentDefFunc = t => ({
  field: 'compId',
  headerName: t('Fields.ComponentID'),
});

export const columnDefAssetFilesStatus: AssetFileColDefFunc = t => ({
  field: 'stat',
  headerName: t('Fields.Status'),
  cellRenderer: FileStatusRenderer,
});

export const columnDefAssetFilesApplied: AssetFileColDefFunc = t => ({
  headerName: t('Fields.Applied'),
  valueGetter: ({ data }: ValueGetterParams<AssetFileDeviceModel>) =>
    data?.app ? 'Applied' : 'Pending',
  cellRenderer: ConfigStatusRenderer,
});

/* Asset Names Column Defs */

export const columnDefAssetNamesName = (t: TFunction): ColDef<AssetNameData> => ({
  field: 'name',
  headerName: t('Fields.AssetName'),
});

export const columnDefAssetNamesSource = (t: TFunction): ColDef<AssetNameData> => ({
  field: 'source',
  headerName: t('Fields.Source'),
});

export const columnDefComponentType = (
  t: TFunction,
  hwType?: string,
): ColDef<AssetComponentModel> => ({
  field: 'type',
  headerName: t('Fields.Component'),
  valueGetter: ({ data }: ValueGetterParams<AssetComponentModel, string>) =>
    data?.type ? getComponentName(t, data.type, hwType) : null,
});

export const columnDefFirmwareVersion: ComponentDefFunc = t => ({
  field: 'firmVer',
  headerName: t('Fields.ComponentSwFw'),
});

export const columnDefHardwareRevision: ComponentDefFunc = t => ({
  field: 'hwRev',
  headerName: t('Fields.HardwareRevision'),
});

export const columnDefSerialNumber: ComponentDefFunc = t => ({
  field: 'serNo',
  headerName: t('Fields.SerialNumber'),
});

/* Asset Fault Column Defs */

type AssetFaultDefFunc = (t: TFunction) => ColDef<AssetFaultDisplayModel>;

export const columnDefAssetFaultCategory: AssetFaultDefFunc = t => ({
  field: 'cat',
  headerName: t('FaultCategory'),
  valueGetter: ({ data }: ValueGetterParams<AssetFaultDisplayModel>): string =>
    getSelfTestCategory(t, data?.cat),
});

export const columnDefAssetFaultLevel: AssetFaultDefFunc = t => ({
  field: 'lvl',
  headerName: t('FaultLevel'),
  cellRenderer: FaultLevelCellRenderer,
});

const FAULT_ID_MIN = 0,
  FAULT_ID_MAX = 96;

const getFaultTest = (t: TFunction, faultId?: number): string => {
  if (typeof faultId !== 'undefined' && faultId >= FAULT_ID_MIN && faultId <= FAULT_ID_MAX) {
    const key = `FaultTest_${faultId}`;
    return t(key);
  }

  return t('Unknown');
};

export const columnDefAssetFaultName: AssetFaultDefFunc = t => ({
  field: 'flt',
  headerName: t('TestName'),
  valueGetter: ({ data }: ValueGetterParams<AssetFaultDisplayModel>): string =>
    getFaultTest(t, data?.flt),
});

/* Generic Column Defs */

export const colDefDuration = <TData extends { dur: TValue }, TValue extends number | null>(
  headerName: string,
): ColDef<TData, TValue> => ({
  field: 'dur' as ColDefField<TData, TValue>,
  headerName,
  cellRenderer: DurationRenderer,
});
